import { get, post } from "../assets/functions";
import dayjs, { utc } from "dayjs";
import weekOfYear from "dayjs/plugin/weekOfYear";
import dayOfYear from "dayjs/plugin/dayOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import router from "../router";
import store from "./index";

dayjs.extend(weekOfYear);
dayjs.extend(dayOfYear);
dayjs.extend(isoWeek);

export default {
    namespaced: true,
    state: {
        view: "week",
        week: 0,
        month: 0,
        day: 0,
        timeFrom: 1,
        timeTo: 24,
        events: [],
        upcoming: [],
        upcomingList: [],
        date: dayjs(),
        interval: null,
        sideBar: false,
        filterGlobal: false,
        filterLocal: true,
        importedCalendars: [],
    },
    mutations: {
        changeTimeFrom(state, time) {
            state.timeFrom = time;
        },
        changeTimeTo(state, time) {
            state.timeTo = time;
        },
        calendarView(state, view) {
            state.view = view;
        },
        incrementMonth(state) {
            state.date = dayjs(state.date).add(1, "month");
            state.month++;
        },
        decrementMonth(state) {
            state.date = dayjs(state.date).subtract(1, "month");
            state.month--;
        },
        incrementWeek(state) {
            state.date = dayjs(state.date).add(1, "week");
            state.week++;
        },
        decrementWeek(state) {
            state.date = dayjs(state.date).subtract(1, "week");
            state.week--;
        },
        incrementDay(state) {
            state.date = dayjs(state.date).add(1, "day");
            state.day++;
        },
        decrementDay(state) {
            state.date = dayjs(state.date).subtract(1, "day");
            state.day--;
        },
        setData(state, response) {
            state.events = response;
        },
        today(state) {
            state.day = dayjs().dayOfYear();
            state.week = dayjs()
                .subtract(1, "day")
                .week();
            state.month = dayjs().month();
            state.date = dayjs();
        },
        loadData(state) {
            state.date = dayjs(state.state);
        },
        setDate(state, response) {
            state.date = response;
        },
        sideBar(state, val) {
            state.sideBar = val;
        },
        setUpcoming(state, response) {
            state.upcoming = response;
        },
        setUpcomingList(state, response) {
            state.upcomingList = response;
        },
        interval(state, val) {
            state.interval = val;
        },
        filterGlobal(state, val) {
            state.filterGlobal = val;
        },
        filterLocal(state, val) {
            state.filterLocal = val;
        },
        setImportedCalendars(state, calendars) {
            state.importedCalendars = calendars || [];
        },
    },
    actions: {
        async getCalendarEvents(context) {
            let view = context.state.view;
            let day = context.state.date;
            if (view === "week") view = "isoWeek";
            if (view === "event") view = "month";
            let start = day.startOf(view).subtract(1, 'day').unix();
            let end = day.endOf(view).add(1, 'day').unix();
            let base = "/api/events/calendar?start=";
            let url = start + "&end=" + end;
            let response = await get(base + url);
            
            try {
                await store.dispatch("booking/fetchRequests", "pending");
                const bookingRequests = store.state.booking.requests || [];
                const bookingEvents = bookingRequests
                    .filter(req => req.status === 'pending' || req.status === 'counter_proposed')
                    .map(req => ({
                        id: `booking-${req.id}`,
                        start: req.status === 'counter_proposed' ? req.counterProposedFrom : req.requestedFrom,
                        end: req.status === 'counter_proposed' ? req.counterProposedTo : req.requestedTo,
                        title: `Meeting Request: ${req.requesterName}`,
                        isBookingRequest: true,
                        bookingRequestId: req.id,
                        bookingRequestStatus: req.status
                    }))
                    .filter(event => event.start >= start && event.start < end);
                
                response = [...(response || []), ...bookingEvents];
            } catch (e) {
                console.error('Error loading booking requests:', e);
            }
            
            context.commit("setData", response);
            return;
        },
        async fetchImportedCalendars(context) {
            const response = await get("/api/imported-calendars");
            if (Array.isArray(response)) {
                context.commit("setImportedCalendars", response);
            }
        },
        async syncImportedCalendar(context, calendarId) {
            try {
                await post(`/api/imported-calendars/${calendarId}/sync`, {});
            } catch (e) {
                console.error('Error syncing imported calendar:', e);
            }
        },
        async syncEnabledImportedCalendars(context) {
            const calendars = context.state.importedCalendars || [];
            const enabledCalendars = calendars.filter(cal => cal.enabled);
            
            if (enabledCalendars.length === 0) {
                return;
            }

            const syncPromises = enabledCalendars.map(cal => 
                context.dispatch('syncImportedCalendar', cal.id)
            );

            try {
                await Promise.all(syncPromises);
                await context.dispatch('fetchImportedCalendars');
                await context.dispatch('getCalendarEvents');
            } catch (e) {
                console.error('Error syncing imported calendars:', e);
            }
        },
        goToLastCalendarView(context) {
            let memoryDate = context.state.date.format("YYYY-MM-DD");
            let memoryView = context.state.view;
            let url = "/calendar/" + memoryView + "/" + memoryDate;
            if (router.currentRoute.name !== "CalendarDate") router.push(url);
            return url;
        },
        async getUpcomingEvents(context) {
            let response = await get("/api/events/upcoming");
            if (!response) return store.dispatch("user/logout");
            store.commit("notifications/raw", response);
            context.commit("setUpcoming", response);
        },
        async getUpcomingList(context) {
            let start = dayjs().unix();
            let end = dayjs().add(14, "day").unix();
            let response = await get("/api/events/calendar?start=" + start + "&end=" + end);
            if (!Array.isArray(response)) return;
            let list = response
                .filter((e) => e && e.start && e.end >= start && !e.isBookingRequest)
                .sort((a, b) => a.start - b.start);
            context.commit("setUpcomingList", list);
        },
        async innitGetUpcomingEvents(context) {
            context.dispatch('clearUpcomingEvents');
            context.dispatch("getUpcomingEvents");
            let interval = setInterval(() => {
                context.dispatch("getUpcomingEvents");
            }, 1000 * 60);
            context.commit("interval", interval);
        },
        async clearUpcomingEvents(context) {
            if (context.state.interval) {
                clearInterval(context.state.interval);
                context.commit("interval", null);
            }
        },
    },
};
